import { createRouter, createWebHistory } from "vue-router";
import { authService } from "../services/auth";
import { platformAdminAuth } from "../services/platform-admin-auth";
import { isQaMode, QA_ROUTE_NAMES } from "../services/app-mode";

const allRoutes = [
  {
    path: "/login",
    name: "Login",
    component: () =>
      isQaMode
        ? import("../views/auth/QaLogin.vue")
        : import("../views/auth/Login.vue"),
    meta: { guestOnly: true },
  },
  // Consola interna de Think: realm de auth completamente aparte del
  // resto de estas rutas (cookie, sesión y guard propios — ver
  // services/platform-admin-auth.js). No usa MainLayout ni requiresAuth.
  {
    path: "/admin/login",
    name: "PlatformAdminLogin",
    component: () => import("../views/admin/AdminLogin.vue"),
  },
  {
    path: "/admin/forgot-password",
    name: "PlatformAdminForgotPassword",
    component: () => import("../views/admin/AdminForgotPassword.vue"),
  },
  {
    path: "/admin/reset-password",
    name: "PlatformAdminResetPassword",
    component: () => import("../views/admin/AdminResetPassword.vue"),
  },
  {
    path: "/admin",
    component: () => import("../layouts/AdminLayout.vue"),
    children: [
      {
        path: "",
        name: "PlatformAdminDashboard",
        component: () => import("../views/admin/AdminDashboard.vue"),
        meta: { staffOnly: true },
      },
      {
        path: "organizaciones",
        name: "PlatformAdminOrganizations",
        component: () => import("../views/admin/AdminOrganizations.vue"),
      },
      {
        path: "planes",
        name: "PlatformAdminPricing",
        component: () => import("../views/admin/AdminPricing.vue"),
        meta: { staffOnly: true },
      },
      {
        path: "retractos",
        name: "PlatformAdminRetractions",
        component: () => import("../views/admin/AdminRetractions.vue"),
        meta: { staffOnly: true },
      },
      {
        path: "boletas-facturas",
        name: "PlatformAdminBillingDocuments",
        component: () => import("../views/admin/AdminBillingDocuments.vue"),
        meta: { staffOnly: true },
      },
      {
        path: "administradores",
        name: "PlatformAdminUsers",
        component: () => import("../views/admin/AdminUsers.vue"),
        meta: { superAdminOnly: true },
      },
    ],
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/auth/Register.vue"),
    meta: { guestOnly: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/auth/ForgotPassword.vue"),
    meta: { guestOnly: true },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("../views/auth/ResetPassword.vue"),
    meta: { guestOnly: true },
  },
  {
    path: "/forgot-email",
    name: "ForgotEmail",
    component: () => import("../views/auth/ForgotEmail.vue"),
    meta: { guestOnly: true },
  },
  {
    path: "/invites/decline",
    name: "InviteDecline",
    component: () => import("../views/auth/InviteDecline.vue"),
  },
  // Solo QA: confirmación antes de iniciar sesión con un enlace de acceso.
  {
    path: "/qa-access",
    name: "QaAccess",
    component: () => import("../views/auth/QaAccessConfirm.vue"),
  },
  {
    path: "/privacidad",
    name: "PrivacyNotice",
    component: () => import("../views/PrivacyNotice.vue"),
  },
  {
    path: "/",
    component: () => import("../layouts/MainLayout.vue"),
    children: [
      {
        path: "",
        name: "Dashboard",
        component: () => import("../views/Dashboard.vue"),
        meta: { title: "Dashboard", icon: "dashboard", requiresAuth: true },
      },
      {
        path: "/signatures/new",
        name: "NewSignature",
        component: () => import("../views/Simulator.vue"),
        meta: { title: "Nueva firma", icon: "draw", requiresAuth: true },
      },
      // OCULTO TEMPORALMENTE: notaría/finiquitos no van en este despliegue a producción (no borrar).
      // {
      //   path: "/signatures/finiquitos",
      //   name: "Finiquitos",
      //   component: () => import("../views/Finiquitos.vue"),
      //   meta: {
      //     title: "Firma de finiquito",
      //     icon: "contract",
      //     requiresAuth: true,
      //   },
      // },
      {
        path: "/simulator",
        redirect: "/signatures/new",
      },
      {
        path: "/tokens",
        name: "Tokens",
        component: () => import("../views/Tokens.vue"),
        meta: {
          title: "API Tokens",
          icon: "vpn_key",
          requiresAuth: true,
          adminOnly: true,
        },
      },
      {
        path: "/documents",
        name: "Documents",
        component: () => import("../views/Documents.vue"),
        meta: {
          title: "Documentos",
          icon: "description",
          requiresAuth: true,
        },
      },
      {
        path: "/downloads",
        redirect: { path: "/documents" },
      },
      {
        path: "/compras",
        name: "Purchases",
        component: () => import("../views/Purchases.vue"),
        meta: {
          title: "Mis compras",
          icon: "receipt",
          requiresAuth: true,
          adminOnly: true,
        },
      },
      {
        path: "/settings",
        name: "Settings",
        component: () => import("../views/Settings.vue"),
        meta: {
          title: "Configuración",
          icon: "settings",
          requiresAuth: true,
        },
      },
      {
        path: "/privacy",
        name: "Privacy",
        component: () => import("../views/Privacy.vue"),
        meta: {
          title: "Privacidad",
          icon: "shield-account-outline",
          requiresAuth: true,
        },
      },
      {
        path: "/tienda",
        name: "Store",
        component: () => import("../views/Store.vue"),
        meta: {
          title: "Tienda",
          icon: "cart-outline",
          requiresAuth: true,
          adminOnly: true,
        },
      },
      {
        path: "/invites/accept",
        name: "InviteAccept",
        component: () => import("../views/InviteAccept.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/pago-retorno",
        name: "PaymentReturn",
        component: () => import("../views/PaymentReturn.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/pago-exitoso",
        name: "PaymentSuccess",
        component: () => import("../views/PaymentSuccess.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/pago-rechazado",
        name: "PaymentRejected",
        component: () => import("../views/PaymentRejected.vue"),
        meta: { requiresAuth: true },
      },
    ],
  },
];

// En QA solo existen las rutas de la consola reducida (ver QA_ROUTE_NAMES);
// los hijos del layout se filtran por nombre y cualquier otra URL vuelve al
// inicio. En producción se usan todas las rutas tal cual.
const qaRoutes = () => [
  ...allRoutes
    .map((route) =>
      route.children
        ? {
            ...route,
            children: route.children.filter(
              (child) => child.redirect || QA_ROUTE_NAMES.has(child.name),
            ),
          }
        : route,
    )
    .filter((route) =>
      route.children
        ? route.children.length > 0 && route.path === "/"
        : QA_ROUTE_NAMES.has(route.name),
    ),
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  // La consola de producción no conoce nada de QA (ni su pantalla de acceso).
  routes: isQaMode
    ? qaRoutes()
    : allRoutes.filter((route) => route.name !== "QaAccess"),
});

authService.setUnauthorizedHandler(() => {
  if (router.currentRoute.value.path !== "/login") {
    return router.push("/login");
  }
});

router.beforeEach(async (to) => {
  // Realm separado: nunca toca authService (sesión de clientes) ni al
  // revés — así un login no puede filtrar estado hacia el otro.
  if (
    !isQaMode &&
    (to.path === "/admin/login" ||
      to.path.startsWith("/admin/") ||
      to.path === "/admin")
  ) {
    const publicAdminPaths = [
      "/admin/login",
      "/admin/forgot-password",
      "/admin/reset-password",
    ];
    if (publicAdminPaths.includes(to.path)) {
      if (to.path === "/admin/login") {
        const isAuthenticated = await platformAdminAuth.ensureInitialized();
        if (isAuthenticated) return { path: "/admin" };
      }
      return true;
    }
    const isAuthenticated = await platformAdminAuth.ensureInitialized();
    if (!isAuthenticated) {
      return { path: "/admin/login", query: { redirect: to.fullPath } };
    }
    // Destino de rebote siempre /admin/organizaciones, no /admin: el
    // dashboard también está restringido (staffOnly) y SUPPORT no puede
    // verlo — organizaciones es la única página que ven los tres roles.
    if (
      to.matched.some((record) => record.meta.superAdminOnly) &&
      !platformAdminAuth.isSuperAdmin()
    ) {
      return { path: "/admin/organizaciones" };
    }
    if (
      to.matched.some((record) => record.meta.staffOnly) &&
      !platformAdminAuth.isStaff()
    ) {
      return { path: "/admin/organizaciones" };
    }
    return true;
  }

  await authService.ensureInitialized();

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const guestOnly = to.matched.some((record) => record.meta.guestOnly);

  if (requiresAuth && !authService.isAuthenticated()) {
    return { path: "/login", query: { redirect: to.fullPath } };
  }

  if (
    to.matched.some((record) => record.meta.adminOnly) &&
    authService.state.user?.role !== "admin"
  ) {
    return { path: "/" };
  }

  if (
    authService.isAuthenticated() &&
    authService.state.user?.mustChangePassword &&
    to.path !== "/settings"
  ) {
    return { path: "/settings", query: { changePassword: "required" } };
  }

  if (guestOnly && authService.isAuthenticated()) {
    return { path: "/" };
  }

  return true;
});

export default router;
